/**
 * @file routes/_app/analytics/financial.tsx
 * @description 財務分析頁。
 *
 * 顯示報價 vs 繳費總金額對比、繳費異常客戶數、月度繳費趨勢（LineChart）。
 * 支援日期範圍篩選 + Excel 匯出。
 *
 * 視覺：PageShell + StatCard + SectionCard + EmptyState（recharts LineChart + BarChart）
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { Button, DatePicker, Space, Spin } from 'antd';
import { message } from '@/lib/antd-app';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { FileDown, RefreshCw, DollarSign, AlertTriangle, TrendingUp } from 'lucide-react';
import { type Dayjs } from 'dayjs';
import { useFinancialSummary } from '@/hooks/useAnalytics';
import { PageShell, SectionCard, StatCard, EmptyState } from '@/components/shell';
import { color, space } from '@/styles/tokens';

const { RangePicker } = DatePicker;

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/analytics/financial')({
  component: AnalyticsFinancialPage,
});

/** 分轉元，格式化顯示 */
function formatNTD(amount: number): string {
  return `NT$${(amount / 100).toLocaleString()}`;
}

/** 分轉元，Y 軸刻度簡化 */
function formatYAxis(value: number): string {
  if (value >= 1_000_000_00) return `${(value / 100_000_000).toFixed(1)}億`;
  if (value >= 100_000_00) return `${(value / 100_000_00).toFixed(1)}千萬`;
  if (value >= 10_000_00) return `${(value / 100_00).toFixed(0)}萬`;
  if (value >= 100_00) return `${(value / 100_00).toFixed(1)}萬`;
  return `${(value / 100).toFixed(0)}`;
}

/** 財務分析頁 */
function AnalyticsFinancialPage() {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [exporting, setExporting] = useState(false);

  const range = {
    from: dateRange?.[0]?.format('YYYY-MM-DD'),
    to: dateRange?.[1]?.format('YYYY-MM-DD'),
  };

  const { data, isLoading, isFetching, refetch } = useFinancialSummary(range);
  const monthlyData = data?.monthlyPayments ?? [];

  /* ── 匯出 ── */
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ type: 'financial' });
      if (range.from) params.set('from', range.from);
      if (range.to) params.set('to', range.to);
      const resp = await fetch(`/api/analytics/export?${params.toString()}`, {
        credentials: 'same-origin',
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zhcb-financial-analytics-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('匯出成功');
    } catch (err) {
      message.error('匯出失敗');
      console.error('export financial analytics failed', err);
    } finally {
      setExporting(false);
    }
  }, [range]);

  return (
    <PageShell
      title="財務分析"
      description="報價金額、繳費情況與異常統計"
      actions={
        <Space>
          <Button
            icon={<RefreshCw size={14} />}
            onClick={() => void refetch()}
            loading={isFetching}
          >
            刷新
          </Button>
          <Button icon={<FileDown size={14} />} loading={exporting} onClick={handleExport}>
            匯出 Excel
          </Button>
        </Space>
      }
    >
      {/* 篩選 */}
      <div style={{ marginBottom: space[4] }}>
        <SectionCard title="篩選條件">
          <RangePicker
            value={dateRange}
            onChange={(v) => setDateRange(v)}
            placeholder={['開始日期', '結束日期']}
            format="YYYY-MM-DD"
          />
        </SectionCard>
      </div>

      {/* KPI 卡片 */}
      {!isLoading && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: space[4],
            marginBottom: space[4],
          }}
        >
          <StatCard
            title="報價總金額"
            value={formatNTD(data?.totalQuotedAmount ?? 0)}
            tone="brand"
            icon={<DollarSign size={18} />}
          />
          <StatCard
            title="已確認繳費"
            value={formatNTD(data?.totalPaidAmount ?? 0)}
            tone="success"
            icon={<TrendingUp size={18} />}
          />
          <StatCard
            title="繳費異常客戶"
            value={data?.paymentAbnormalClients ?? 0}
            suffix="家"
            tone={data?.paymentAbnormalClients ? 'danger' : 'neutral'}
            icon={<AlertTriangle size={18} />}
          />
        </div>
      )}

      {/* 圖表 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space[4] }}>
        {/* 月度繳費趨勢 LineChart */}
        <SectionCard title="月度繳費趨勢">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: space[8] }}>
              <Spin />
            </div>
          ) : monthlyData.length === 0 ? (
            <EmptyState title="暫無繳費資料" size="sm" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthlyData} margin={{ top: 8, right: 16, bottom: 8, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={formatYAxis} />
                <RechartTooltip
                  formatter={(v, n) =>
                    n === 'amount'
                      ? [formatNTD(Number(v)), '繳費金額']
                      : [`${String(v)} 筆`, '筆數']
                  }
                />
                <Legend formatter={(v) => (v === 'amount' ? '繳費金額' : '繳費筆數')} />
                <Line
                  type="monotone"
                  dataKey="amount"
                  name="amount"
                  stroke={color.brand.primary}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        {/* 月度繳費筆數 BarChart */}
        <SectionCard title="月度繳費筆數">
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: space[8] }}>
              <Spin />
            </div>
          ) : monthlyData.length === 0 ? (
            <EmptyState title="暫無繳費資料" size="sm" />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthlyData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <RechartTooltip formatter={(v) => [`${String(v)} 筆`, '繳費筆數']} />
                <Bar
                  dataKey="count"
                  name="繳費筆數"
                  fill={color.state.success}
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>
      </div>
    </PageShell>
  );
}
